import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import katex from 'katex';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – mhchem has no separate type declarations; it augments katex at runtime
import 'katex/contrib/mhchem';
import { ChemFormulaView } from './chem-formula-view';
import { escapeUnescapedPercent } from './latex-percent';

export interface ChemFormulaOptions {
  onEditRequest: (formula: string, update: (newFormula: string) => void) => void;
}

/** Arrow forms mhchem understands, longest first so `<=>` is never read as `<=`. A token counts as
 * an arrow if it STARTS with one of these, which keeps an annotated `->[catalyst]` in one piece. */
const CHEM_ARROWS = ['<=>>', '<<=>', '<=>', '<->', '->', '<-', '=>', '<='];

/**
 * Splits a reaction at its top-level operators so the rendered result can wrap.
 *
 * KaTeX permits a line break only between `.base` spans, and `\ce{...}` renders as exactly one no
 * matter how long it is — which is why a reaction could never wrap while an equation always could.
 * Rendering the reaction as several `\ce{}` groups joined by `\allowbreak` gives it the same
 * structure KaTeX already builds for `E = mc^2 + ...`, so it breaks by the same mechanism: only when
 * the line actually runs out, and never inside a formula unit.
 *
 * Break BEFORE an arrow and AFTER a `+`, which is TeX's own convention for relations and binary
 * operators — a wrapped reaction reads "2H2 + O2" / "-> 2H2O".
 *
 * Splitting on spaces at bracket depth zero is what makes this safe, and it is mhchem's own rule:
 * a `+` with spaces around it is an operator, one attached to a formula (`Na+`, `Ca^2+`) is a
 * charge and is never a token of its own. Anything unbalanced returns null and renders whole.
 */
function splitReactionAtOperators(formula: string): string[] | null {
  const tokens: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of formula) {
    if (ch === '{' || ch === '[' || ch === '(') depth++;
    else if (ch === '}' || ch === ']' || ch === ')') depth--;
    if (depth < 0) return null;
    if (ch === ' ' && depth === 0) { if (current) { tokens.push(current); current = ''; } continue; }
    current += ch;
  }
  if (depth !== 0) return null;
  if (current) tokens.push(current);

  const groups: string[][] = [[]];
  for (const token of tokens) {
    const last = groups[groups.length - 1]!;
    if (CHEM_ARROWS.some(a => token.startsWith(a)) && last.length > 0) {
      groups.push([token]);
    } else {
      last.push(token);
      if (token === '+') groups.push([]);
    }
  }
  const parts = groups.filter(g => g.length > 0).map(g => g.join(' '));
  return parts.length > 1 ? parts : null;
}

/** `[color:content]`, Agavi's own colour syntax. CSS names or hex. */
const COLOR_WRAP = /\[([a-zA-Z]+|#[0-9a-fA-F]{3,6}):([^\]]+)\]/g;

/**
 * One reaction part, as `\ce{}` groups with any colour wrapped AROUND them.
 *
 * ── WHY COLOUR GOES OUTSIDE, AND NOT INSIDE ───────────────────────────────────────────────────
 * It used to be substituted in place, so `[c:2H2O]` became `\ce{\textcolor{c}{2H2O}}`. mhchem
 * does not apply its chemistry formatting inside a `\textcolor` group, so the colour silently
 * cost the content its chemistry: `2H2O` came out italic, in maths face, with no subscript, while
 * the uncoloured species beside it rendered properly. Colouring a species made it stop looking
 * like one.
 *
 * It also broke outright for seven of the sixty palette tones. mhchem expands `\ce{}` as a
 * macro, and inside a macro body `#1` is a PARAMETER REFERENCE — so every hex beginning with a
 * digit (the greens, teals and blues) was read as parameter 1 followed by the rest, and the whole
 * reaction failed to parse. A teacher picked a green and their reaction disappeared.
 *
 * Wrapping instead of substituting fixes both: `\textcolor{c}{\ce{2H2O}}` keeps the `\ce{}`
 * doing chemistry, and the hex never reaches mhchem's expander.
 */
function emitPart(part: string): string {
  let out = '';
  let last = 0;
  COLOR_WRAP.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = COLOR_WRAP.exec(part)) !== null) {
    const before = part.slice(last, m.index);
    if (before) out += `\\ce{${before}}`;
    out += `\\textcolor{${m[1]}}{\\ce{${m[2]}}}`;
    last = m.index + m[0].length;
  }
  const rest = part.slice(last);
  if (rest) out += `\\ce{${rest}}`;
  return out || `\\ce{${part}}`;
}

/**
 * THE SPACE A RELATION LOSES WHEN THE REACTION IS CUT IN FRONT OF IT.
 *
 * TeX sets a relation with thickmuskip on BOTH sides. The split puts an arrow at the START of
 * its own `\ce{}` group, so there is nothing to its left to be spaced from, and the join could
 * not supply it either: it ended in a plain space, and a plain space in maths mode is ignored.
 * `CO2 + H2O ->[\Delta] CH2O` came out with the arrow jammed against the CO2 — the one join in
 * a reaction that most needs air.
 *
 * `\;` IS thickmuskip, so it puts back exactly what the cut took. Measured against the same
 * reaction rendered whole: 188.25px either way, where the joined version was 182.88px.
 *
 * Only before a RELATION. A part that follows a `+` lost nothing, because the `+` sits at the
 * end of the part before it and has already set its own space — adding one there overshot the
 * whole formula by the same amount it fixed.
 */
function joinParts(parts: string[]): string {
  return parts
    .map(emitPart)
    .reduce((out, piece, i) =>
      out + (CHEM_ARROWS.some(a => parts[i]!.startsWith(a)) ? '\\;' : '') + '\\allowbreak ' + piece);
}

export function renderChemFormula(formula: string): { html: string; error: string | null } {
  try {
    // State symbols → italic (KaTeX mhchem renders them upright by default). Applied before the
    // split, as it always was, so where a reaction may wrap does not change. Colour markers are
    // left in place for `emitPart` to wrap around.
    const processed = formula
      .replace(/\((l|g|s|aq)\)/g, (_, sym) => `{\\textit{(${sym})}}`)
      /*
       * A GLOSS IS SET SMALLER THAN THE FORMULA IT EXPLAINS.
       *
       * `\underset` makes its box as wide as the WIDER of the two, so `calcium carbonate`
       * under `CaCO3` widened the species to the width of the words: the formula floated in
       * the middle of its own box and the arrow after it was pushed away. Reported as the
       * label being off and belonging to the atoms rather than the molecule.
       *
       * Done HERE and not in the stored string, for the same reason the state symbols are:
       * it is how the reaction is SET, not what it says, and the editor's own field draws the
       * gloss small by its own stylesheet already.
       */
      /* `\raisebox` drops it clear of the species: `\underset`'s own gap is tight enough that
         the words touched the formula they explain. Reported. One replace over the whole
         group, because opening a brace in one pass and closing it in another is a pair that
         can only be read by holding both in your head. */
      .replace(/\\underset\{\\text\{([^}]*)\}\}/g,
        (_w, words: string) => `\\underset{\\raisebox{-1pt}{\\scriptsize\\text{${words}}}}`);
    const parts = splitReactionAtOperators(processed);
    const source = parts ? joinParts(parts) : emitPart(processed);
    // A bare `%` is a KaTeX comment and silently eats the rest of the source — see latex-percent.ts.
    const html = katex.renderToString(escapeUnescapedPercent(source), {
      throwOnError: true,
      displayMode: false,
    });
    return { html, error: null };
  } catch (err) {
    const raw = err instanceof Error ? err.message : 'Invalid formula';
    const first = raw.split('\n')[0] ?? raw;
    return { html: '', error: first.replace(/^KaTeX parse error:\s*/i, '') };
  }
}

// Post-processes the HTML string from editor.getHTML() so that
// chem-formula spans contain pre-rendered KaTeX HTML rather than the
// plain-text formula fallback. This makes the preview work without JS.
export function processChemHTML(html: string): string {
  if (!html.includes('data-chem-formula')) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll<HTMLElement>('[data-chem-formula]').forEach((span) => {
    const formula = span.getAttribute('data-chem-formula') ?? '';
    if (!formula) return;
    const { html: rendered } = renderChemFormula(formula);
    if (rendered) span.innerHTML = rendered;
  });
  return div.innerHTML;
}

export const ChemFormula = Node.create<ChemFormulaOptions>({
  name: 'chemFormula',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      onEditRequest: () => {},
    };
  },

  addAttributes() {
    return {
      formula: { default: '' },
    };
  },

  parseHTML() {
    return [
      { tag: 'span[data-chem-formula]', getAttrs: (el) => ({ formula: (el as HTMLElement).getAttribute('data-chem-formula') ?? '' }) },
      { tag: 'div[data-chem-formula]',  getAttrs: (el) => ({ formula: (el as HTMLElement).getAttribute('data-chem-formula') ?? '' }) },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-chem-formula': node.attrs.formula, contenteditable: 'false', class: 'chem-formula' },
        HTMLAttributes,
      ),
      String(node.attrs.formula),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChemFormulaView);
  },
});
